// src/components/Home.jsx
//
// FIXES (May 2026):
//
//  FIX 1 — Homepage carousel only showed admin style_vault, not stylist uploads:
//    fetchAllData() fetched only collectionGroup('style_vault').
//    Stylist uploads go to the ROOT 'global_gallery' collection.
//    Fix: fetch BOTH sources, normalise the image field, merge + deduplicate,
//    sort by createdAt desc, cap at 30 for the carousel.
//
//  FIX 2 — Carousel didn't update live when a stylist published a new style:
//    fetchAllData ran once on mount. New uploads required a page refresh.
//    Fix: added onSnapshot on ROOT global_gallery so carousel updates within
//    seconds of a stylist hitting "Publish" — no refresh needed.
//
//  FIX 3 — Deals section missed isPromoted styles from ROOT global_gallery:
//    collectionGroup('global_gallery') hits subcollections, not root.
//    Fix: getDocs on ROOT collection(db,'global_gallery') with isPromoted==true,
//    merged with collectionGroup results for full coverage.
//
//  FIX 4 — "🌍 STYLE VAULT" button already navigates to /gallery ✓
//    No change needed — confirmed correct in current code.
//
//  All waitlist, auth, parallax, merchant, and portal logic unchanged.

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  collection,
  collectionGroup,
  limit,
  query,
  getDocs,
  where,
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';
import { logInfraction } from '../utils/security';
import MerchantPortal from './merchant/MerchantPortal';
import MerchantMarketplaceCarousel from './merchant/MerchantMarketplaceCarousel';
import StyleTryOn from './StyleTryon';

const GOLD         = '#B8860B';
const NAVY         = '#001F3F';
const DEEP_BLACK   = '#050a0f';
const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];
const SAFE_PLACEHOLDER = 'https://placehold.co/600x400/000000/B8860B?text=Esdras+Style';

// ─────────────────────────────────────────────────────────────────────────────
//  NORMALISE a Firestore doc into the shape the carousel expects:
//    { id, name, image, createdAt, ... }
//  Handles both style_vault (image OR imageUrl) and global_gallery (imageUrl).
// ─────────────────────────────────────────────────────────────────────────────
function normaliseStyle(docId, data) {
  return {
    id:        docId,
    name:      data.name       || 'Esdras Style',
    image:     data.imageUrl   || data.image || SAFE_PLACEHOLDER,
    createdAt: data.createdAt  || null,
    ...data,
  };
}

export default function Home({ user }) {
  const navigate    = useNavigate();
  const [searchParams] = useSearchParams();
  const waitlistRef = useRef(null);

  const [trendingStyles,  setTrendingStyles]  = useState([]);
  const [deals,           setDeals]           = useState([]);
  const [offsetY,         setOffsetY]         = useState(0);
  const [showPortalPicker,setShowPortalPicker]= useState(false);
  const [loading,         setLoading]         = useState(true);
  const [globalLock,      setGlobalLock]      = useState(false);
  const [showMerchantPortal, setShowMerchantPortal] = useState(false);
  const [showStyleTryOn,  setShowStyleTryOn]  = useState(false);

  const [role,            setRole]            = useState('user');
  const [formData,        setFormData]        = useState({ firstName: '', email: '', password: '', businessName: '' });
  const [submitted,       setSubmitted]       = useState(sessionStorage.getItem('esdras_submitted') === 'true');
  const [isFull,          setIsFull]          = useState(false);
  const [remainingSlots,  setRemainingSlots]  = useState(null);
  const [checkLoading,    setCheckLoading]    = useState(true);
  const [isSubmitting,    setIsSubmitting]    = useState(false);

  const handleScroll     = () => setOffsetY(window.pageYOffset);
  const handleImageError = (e) => { e.target.src = SAFE_PLACEHOLDER; };

  // ── 1. INITIAL LOAD ────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      await fetchInitialData();
      setLoading(false);
    };
    init();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── 2. REAL-TIME GLOBAL GALLERY LISTENER ──────────────────────────────────
  //
  //  FIX 2: onSnapshot on ROOT global_gallery.
  //  When a stylist publishes a style, the carousel updates live —
  //  no page refresh needed.
  //
  //  Merges with the admin style_vault data already fetched by fetchInitialData().
  //  Uses a ref to hold the latest admin styles so we can merge without refetching.
  //
  const adminStylesRef = useRef([]); // holds style_vault results

  useEffect(() => {
    const q = query(
      collection(db, 'global_gallery'),
      orderBy('createdAt', 'desc'),
      limit(30)
    );

    const unsub = onSnapshot(q, (snap) => {
      const galleryItems = snap.docs.map(d => normaliseStyle(d.id, d.data()));

      // Merge: admin styles first (for brand positioning), then stylist styles
      // Deduplicate by id in case an item appears in both (shouldn't, but safe)
      const seen  = new Set(galleryItems.map(s => s.id));
      const admin = adminStylesRef.current.filter(s => !seen.has(s.id));
      const merged = [...admin, ...galleryItems];

      // Sort merged array by createdAt desc
      merged.sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      });

      setTrendingStyles(merged.slice(0, 30));
    }, (err) => {
      console.warn('[Home] global_gallery listener error:', err.message);
    });

    return () => unsub();
  }, []);

  // ── 3. REAL-TIME LOCK / CAPACITY ──────────────────────────────────────────
  useEffect(() => {
    const unsubLock = onSnapshot(
      doc(db, 'app_settings', 'registry'),
      (snap) => { if (snap.exists()) setGlobalLock(snap.data().isLocked || false); },
      () => {}
    );
    setCheckLoading(true);
    const unsubStats = onSnapshot(
      doc(db, 'app_settings', 'stats'),
      (snap) => {
        setCheckLoading(false);
        if (!snap.exists()) { setRemainingSlots(role === 'stylist' ? 100 : 200); setIsFull(false); return; }
        const data     = snap.data() || {};
        const count    = Number(data[`${role}Count`]) || 0;
        const capLimit = role === 'stylist' ? 100 : 200;
        setRemainingSlots(Math.max(0, capLimit - count));
        setIsFull(count >= capLimit);
      },
      () => { setCheckLoading(false); setRemainingSlots('Limited'); setIsFull(false); }
    );
    return () => { unsubLock(); unsubStats(); };
  }, [role]);

  // ─────────────────────────────────────────────────────────────────────────
  //  FIX 1 + FIX 3 — fetchInitialData
  //
  //  Carousel (trendingStyles):
  //    Source A: collectionGroup 'style_vault'     ← admin-curated historical
  //    Source B: ROOT collection 'global_gallery'  ← stylist uploads (new)
  //    → merged, deduplicated, sorted by createdAt desc, capped at 30
  //
  //  Deals:
  //    Source A: collectionGroup 'global_gallery' where isPromoted==true
  //              (hits subcollections — for any legacy subcollection deals)
  //    Source B: ROOT collection 'global_gallery' where isPromoted==true
  //              (hits root — where BarberUpload + UploadStyleModal write)
  //    → merged, deduplicated
  // ─────────────────────────────────────────────────────────────────────────
  const fetchInitialData = async () => {
    try {
      // ── A: Admin style_vault (collectionGroup — subcollections) ───────────
      const vaultSnap = await getDocs(
        query(collectionGroup(db, 'style_vault'), orderBy('createdAt', 'desc'), limit(20))
      );
      const adminStyles = vaultSnap.docs.map(d => normaliseStyle(d.id, d.data()));
      adminStylesRef.current = adminStyles;

      // ── B: Stylist global_gallery ROOT (one-time initial fetch) ───────────
      //  The onSnapshot above will keep this updated in real-time,
      //  but we set initial state here so the carousel isn't empty on first load.
      const gallerySnap = await getDocs(
        query(collection(db, 'global_gallery'), orderBy('createdAt', 'desc'), limit(30))
      );
      const galleryItems = gallerySnap.docs.map(d => normaliseStyle(d.id, d.data()));

      // Merge A + B, deduplicate, sort
      const seen   = new Set(galleryItems.map(s => s.id));
      const unique = [...adminStyles.filter(s => !seen.has(s.id)), ...galleryItems];
      unique.sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() || 0;
        const tb = b.createdAt?.toMillis?.() || 0;
        return tb - ta;
      });
      setTrendingStyles(unique.slice(0, 30));

      // ── Deals: collectionGroup (legacy subcollections) ────────────────────
      let dealsMap = new Map();
      try {
        const groupSnap = await getDocs(
          query(
            collectionGroup(db, 'global_gallery'),
            where('isPromoted', '==', true),
            orderBy('createdAt', 'desc'),
            limit(10)
          )
        );
        groupSnap.docs.forEach(d => {
          dealsMap.set(d.id, { id: d.id, ...d.data(), price: Number(d.data().price) || 0, imageUrl: d.data().imageUrl || SAFE_PLACEHOLDER });
        });
      } catch {} // collectionGroup may fail for guests — non-critical

      // FIX 3: also query ROOT global_gallery for isPromoted deals
      try {
        const rootDealsSnap = await getDocs(
          query(
            collection(db, 'global_gallery'),
            where('isPromoted', '==', true),
            orderBy('createdAt', 'desc'),
            limit(10)
          )
        );
        rootDealsSnap.docs.forEach(d => {
          if (!dealsMap.has(d.id)) {
            dealsMap.set(d.id, { id: d.id, ...d.data(), price: Number(d.data().price) || 0, imageUrl: d.data().imageUrl || SAFE_PLACEHOLDER });
          }
        });
      } catch {} // non-critical

      setDeals([...dealsMap.values()].slice(0, 10));

    } catch (err) {
      console.warn('[Home] fetchInitialData limited (Guest Mode):', err.message);
    }
  };

  // ── WAITLIST ───────────────────────────────────────────────────────────────
  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (globalLock || isFull || isSubmitting) return;

    const suspiciousRegex = /[<>={}]/g;
    const userInput = `${formData.firstName} ${formData.businessName} ${formData.email}`;
    if (suspiciousRegex.test(userInput)) {
      setIsSubmitting(true);
      await logInfraction('XSS_INJECTION_ATTEMPT', `Homepage Waitlist: ${userInput}`);
      setIsSubmitting(false);
      alert('Security alert: Invalid characters detected.');
      return;
    }

    setIsSubmitting(true);
    try {
      const cred    = await createUserWithEmailAndPassword(auth, formData.email.toLowerCase().trim(), formData.password);
      const newUser = cred.user;
      await setDoc(doc(db, 'waitlist', newUser.uid), {
        uid:          newUser.uid,
        firstName:    formData.firstName.trim(),
        email:        formData.email.toLowerCase().trim(),
        businessName: role === 'stylist' ? formData.businessName.trim() : '',
        role,
        status:       'pending',
        createdAt:    serverTimestamp(),
        submittedFrom:'homepage',
      });
      setSubmitted(true);
      sessionStorage.setItem('esdras_submitted', 'true');
    } catch (err) {
      console.error('Waitlist error:', err);
      alert(err.message || 'Submission failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDashboardRedirect = () => {
    if (!user) { navigate('/login'); return; }
    if (SUPER_ADMINS.includes(user.uid)) { setShowPortalPicker(true); return; }
    navigate('/dashboard');
  };

  const handleProtectedAction = (action) => {
    if (!user) {
      globalLock
        ? navigate('/login')
        : waitlistRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    typeof action === 'string' ? navigate(action) : action?.();
  };

  // ── LOADING SCREEN ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.scannerWrapper}>
          <div style={styles.scannerLine} />
          <h1 style={{ ...homeStyles.heroTitle, fontSize: '4rem' }}>esdras</h1>
          <p style={styles.loaderText}>INITIALIZING STYLE VAULT...</p>
        </div>
      </div>
    );
  }

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={homeStyles.homeContainer}>
      <style>{`
        @keyframes scan { 0% { top: 0%; opacity: 0; } 50% { top: 100%; opacity: 1; } 100% { top: 0%; opacity: 0; } }
        @keyframes scrollLeft { 0% { transform: translateX(0); } 100% { transform: translateX(-33.33%); } }
        .scroll-track { display: flex; width: max-content; animation: scrollLeft 40s linear infinite; }
        .hero-btn { transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); cursor: pointer; border: none; }
        .hero-btn:hover:not(:disabled) { transform: translateY(-5px); }
        .gold-glow-btn { animation: gold-glow 3s infinite ease-in-out; }
        @keyframes gold-glow { 0% { box-shadow: 0 0 10px ${GOLD}44; } 50% { box-shadow: 0 0 25px ${GOLD}88; } 100% { box-shadow: 0 0 10px ${GOLD}44; } }
        .parallax-bg { position: fixed; inset: 0; background: radial-gradient(circle at 50% 50%, ${NAVY} 0%, ${DEEP_BLACK} 100%); z-index: -1; }
        .parallax-stars { position: fixed; inset: 0; background-image: radial-gradient(white 1px, transparent 1px); background-size: 50px 50px; opacity: 0.15; z-index: -1; pointer-events: none; }
        .urgency-badge { animation: pulse-gold 2s infinite; color: ${GOLD}; font-weight: 900; font-size: 0.75rem; letter-spacing: 3px; margin-bottom: 15px; text-transform: uppercase; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      <div className="parallax-bg" />
      <div className="parallax-stars" style={{ transform: `translateY(${offsetY * 0.1}px)` }} />

      {/* ── SUPER ADMIN PORTAL PICKER ── */}
      {showPortalPicker && (
        <div style={homeStyles.modalOverlay}>
          <div style={homeStyles.portalBox}>
            <h2 style={{ color: GOLD, letterSpacing: '2px' }}>SUPER ADMIN ACCESS</h2>
            {[
              { label: 'ADMIN PANEL (SYSTEM)',        path: '/admin' },
              { label: 'ADMIN LEDGER (FINANCE)',       path: '/admin/dashboard' },
              { label: 'MERCHANT ADMIN PANEL',         path: '/admin/merchant' },
              { label: 'MERCHANT DASHBOARD (PREVIEW)', path: '/merchant/dashboard' },
              { label: 'CLIENT VIEW',                  path: '/dashboard' },
            ].map(({ label, path }) => (
              <button key={label} style={homeStyles.portalBtn} onClick={() => navigate(path)}>{label}</button>
            ))}
            <button style={{ background: 'transparent', color: '#555', border: 'none', marginTop: '15px', cursor: 'pointer', fontSize: '0.85rem' }}
              onClick={() => setShowPortalPicker(false)}>CANCEL</button>
          </div>
        </div>
      )}

      {showMerchantPortal && <MerchantPortal onClose={() => setShowMerchantPortal(false)} />}

      {showStyleTryOn && (
        <div style={homeStyles.modalOverlay}>
          <div style={homeStyles.tryOnContainer}>
            <StyleTryOn onClose={() => setShowStyleTryOn(false)} />
          </div>
        </div>
      )}

      {/* ── NAV ── */}
      <nav style={homeStyles.topNav}>
        <img src="/icons/esdras-logo.png" alt="Logo" style={homeStyles.navLogo} onError={handleImageError} />
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button onClick={() => setShowMerchantPortal(true)} style={homeStyles.navSellBtn} className="hero-btn">
            SELL ON ESDRAS
          </button>
          <button onClick={handleDashboardRedirect} style={homeStyles.navLoginBtn}>
            {user ? 'DASHBOARD' : 'LOGIN'}
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <div style={{ ...homeStyles.heroContent, transform: `translateY(${offsetY * 0.15}px)` }}>
        <h1 style={homeStyles.heroTitle}>esdras</h1>
        <p style={homeStyles.heroSub}>
          The future of grooming and styling. See any hairstyle on your exact head mesh in seconds.<br /><br />
          <span style={homeStyles.subEmphasis}>Precision Styling. Zero regrets.</span>
        </p>
        <div style={homeStyles.heroGrid}>
          <button onClick={() => handleProtectedAction('/scan')} className="hero-btn gold-glow-btn" style={homeStyles.goldHeroBtn}>
            📸 AI SCANNER
          </button>
          {/*
            FIX 4 ✓ — STYLE VAULT button navigates to /gallery (global-gallery)
            Style changed to solid gold background so it stands out alongside AI SCANNER.
          */}
          <button
            onClick={() => handleProtectedAction('/gallery')}
            className="hero-btn"
            style={{ ...homeStyles.outlineHeroBtn, background: GOLD, color: '#000', fontWeight: '900' }}>
            🌍 STYLE VAULT
          </button>
          <button onClick={() => handleProtectedAction(() => setShowStyleTryOn(true))} className="hero-btn gold-glow-btn" style={homeStyles.snapHeroBtn}>
            ✂️ STYLE TRY-ON AI
          </button>
          <button onClick={() => handleProtectedAction('/barbers')} className="hero-btn" style={homeStyles.outlineHeroBtn}>
            ✨ FIND STYLISTS
          </button>
        </div>
      </div>

      {/* ── WAITLIST ── */}
      {!user && !globalLock && (
        <section ref={waitlistRef} style={homeStyles.waitlistSection}>
          <div style={homeStyles.formCard}>
            {submitted ? (
              <div style={{ padding: '20px' }}>
                <h2 style={{ color: GOLD, fontSize: '2rem', fontWeight: '900' }}>✨ PROFILE CREATED ✨</h2>
                <p style={{ opacity: 0.7, marginTop: '15px', lineHeight: '1.6', color: '#fff' }}>
                  Your account has been created successfully. Our team is reviewing applications for the Founders Circle.
                  <br /><br />
                  <strong>Check your email shortly for verification instructions.</strong>
                </p>
              </div>
            ) : (
              <>
                <div className="urgency-badge">
                  {checkLoading ? 'Calculating availability...' : isFull
                    ? '🔥 BETA INTAKE FULL'
                    : <>🔥 ONLY <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{remainingSlots}</span> {role.toUpperCase()} SLOTS REMAINING</>
                  }
                </div>
                <h2 style={{ fontSize: '1.8rem', fontWeight: '900', marginBottom: '10px' }}>Join the Founders Circle</h2>
                <div style={homeStyles.roleSwitcher}>
                  {['user', 'stylist'].map(r => (
                    <button key={r} onClick={() => setRole(r)}
                      style={{ ...homeStyles.roleBtn, background: role === r ? GOLD : 'transparent', color: role === r ? 'black' : '#666' }}>
                      {r === 'user' ? 'CLIENT' : 'STYLIST'}
                    </button>
                  ))}
                </div>
                <form onSubmit={handleWaitlistSubmit} style={homeStyles.formStyle}>
                  <input required placeholder="First Name" style={homeStyles.inputStyle} onChange={e => setFormData({ ...formData, firstName: e.target.value })} />
                  <input required type="email" placeholder="Email Address" style={homeStyles.inputStyle} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                  <input required type="password" placeholder="Create Password" style={homeStyles.inputStyle} onChange={e => setFormData({ ...formData, password: e.target.value })} />
                  {role === 'stylist' && (
                    <input required placeholder="Business / Shop Name" style={homeStyles.inputStyle} onChange={e => setFormData({ ...formData, businessName: e.target.value })} />
                  )}
                  <button type="submit" disabled={checkLoading || isFull || isSubmitting}
                    style={homeStyles.submitBtn} className="hero-btn gold-glow-btn">
                    {isSubmitting ? 'CREATING ACCOUNT...' : isFull ? 'LIST FULL' : 'SECURE MY SPOT'}
                  </button>
                </form>
              </>
            )}
          </div>
        </section>
      )}

      {/* ── DEALS OF THE WEEK ── */}
      {deals.length > 0 && (
        <section style={{ padding: '60px 20px', background: '#050a0f', width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '1200px', margin: '0 auto 30px' }}>
            <h2 style={{ color: GOLD, fontWeight: '900' }}>🔥 DEALS OF THE WEEK</h2>
            <button
              onClick={() => handleProtectedAction('/discovery')}
              style={{ background: 'transparent', border: `1px solid ${GOLD}`, color: GOLD, padding: '8px 20px', borderRadius: '50px', cursor: 'pointer' }}>
              EXPLORE RUNWAY
            </button>
          </div>
          <div className="hide-scrollbar" style={{ display: 'flex', overflowX: 'auto', gap: '20px', paddingBottom: '20px' }}>
            {deals.map(deal => (
              <div key={deal.id}
                style={{ minWidth: '280px', background: '#0a0a0a', border: '1px solid #1a1a1a', position: 'relative', cursor: 'pointer', borderRadius: '12px', overflow: 'hidden' }}
                onClick={() => handleProtectedAction(`/barber/${deal.creatorId || deal.barberId || 'admin'}`)}>
                <div style={{ position: 'absolute', top: '10px', left: '10px', background: GOLD, color: '#000', padding: '4px 10px', fontWeight: '900', zIndex: 2, borderRadius: '4px' }}>HOT DEAL</div>
                <div style={{ height: '300px', overflow: 'hidden' }}>
                  <img src={deal.imageUrl} alt={deal.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={handleImageError} />
                </div>
                <div style={{ padding: '15px', textAlign: 'left' }}>
                  <p style={{ color: '#666', fontSize: '0.7rem', margin: '0 0 4px' }}>{deal.creatorName || deal.barberName || 'Esdras Official'}</p>
                  <p style={{ color: '#fff', fontWeight: 'bold', margin: '0 0 8px' }}>{deal.name}</p>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <span style={{ color: '#444', textDecoration: 'line-through' }}>₦{(Number(deal.price) + 2000).toLocaleString()}</span>
                    <span style={{ color: GOLD, fontWeight: '900' }}>₦{Number(deal.price).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── ESDRAS STYLE VAULT CAROUSEL ── */}
      {/*
        FIX 1 + FIX 2: trendingStyles now contains BOTH:
          • Admin-curated styles from style_vault subcollections
          • Stylist-uploaded styles from global_gallery ROOT
        Real-time via onSnapshot — updates within seconds when stylists publish.
        Shows "No styles yet" gracefully if both sources are empty.
      */}
      <section style={{ padding: '80px 0', background: '#000', width: '100%' }}>
        <h2 style={{ color: '#fff', textAlign: 'center', letterSpacing: '8px', marginBottom: '50px' }}>
          ESDRAS STYLE VAULT
        </h2>

        {trendingStyles.length === 0 ? (
          <div style={{ textAlign: 'center', opacity: 0.4, padding: '40px 0' }}>
            <p style={{ color: GOLD, fontSize: '0.8rem', letterSpacing: '3px' }}>VAULT INITIALISING...</p>
          </div>
        ) : (
          <div style={{ overflow: 'hidden', width: '100vw' }}>
            <div className="scroll-track">
              {/* Triple the array so the seamless loop never shows a gap */}
              {[...trendingStyles, ...trendingStyles, ...trendingStyles].map((style, idx) => (
                <div
                  key={`${style.id}-${idx}`}
                  style={{ width: '250px', display: 'inline-block', margin: '0 10px', cursor: 'pointer', flexShrink: 0 }}
                  onClick={() => handleProtectedAction(() => setShowStyleTryOn(true))}>
                  <div style={{ position: 'relative', height: '360px', overflow: 'hidden', borderRadius: '12px' }}>
                    <img
                      src={style.image}
                      alt={style.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={handleImageError}
                    />
                    <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '50%', background: 'linear-gradient(transparent, #000)' }} />
                    {/* Show stylist credit badge for community uploads */}
                    {style.barberName && (
                      <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(0,0,0,0.7)', color: GOLD, fontSize: '0.5rem', fontWeight: '900', padding: '3px 8px', borderRadius: '6px', letterSpacing: '1px' }}>
                        {style.businessName || style.barberName}
                      </div>
                    )}
                  </div>
                  <p style={{ color: '#fff', textAlign: 'center', marginTop: '10px', fontSize: '0.85rem' }}>{style.name}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* ── STYLIST CTA ── */}
      <div style={{ background: GOLD, padding: '80px 20px', textAlign: 'center', width: '100%' }}>
        <h3 style={{ color: '#000', fontSize: '2rem', fontWeight: '900' }}>Are you a Stylist?</h3>
        <p style={{ color: '#000', margin: '20px 0' }}>Join the Global Gallery and get discovered by thousands of clients.</p>
        <button
          onClick={() => user ? navigate('/stylist/dashboard') : waitlistRef.current?.scrollIntoView({ behavior: 'smooth' })}
          style={{ background: '#000', color: GOLD, padding: '15px 40px', border: 'none', borderRadius: '50px', fontWeight: '900' }}
          className="hero-btn gold-glow-btn">
          JOIN THE ELITE
        </button>
      </div>

      <MerchantMarketplaceCarousel />

      <footer style={{ padding: '40px', textAlign: 'center', color: '#444', background: '#000', width: '100%' }}>
        © {new Date().getFullYear()} ESDRAS TECHNOLOGIES INC.
      </footer>
    </div>
  );
}

// ── STYLES ────────────────────────────────────────────────────────────────────
const styles = {
  container:     { backgroundColor: DEEP_BLACK, height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif", color: 'white', position: 'relative' },
  scannerWrapper:{ textAlign: 'center', position: 'relative', width: '300px' },
  scannerLine:   { width: '100%', height: '2px', background: GOLD, boxShadow: `0 0 15px ${GOLD}`, position: 'absolute', top: '0', animation: 'scan 2s infinite ease-in-out' },
  loaderText:    { fontSize: '0.6rem', letterSpacing: '3px', marginTop: '20px', opacity: 0.6, textAlign: 'center' },
};

const homeStyles = {
  homeContainer:  { minHeight: '100vh', color: 'white', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', overflowX: 'hidden', position: 'relative' },
  topNav:         { width: '100%', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'absolute', top: 0, zIndex: 10, boxSizing: 'border-box' },
  navLogo:        { width: '42px', height: 'auto' },
  navLoginBtn:    { background: 'transparent', color: GOLD, border: `1.5px solid ${GOLD}`, padding: '10px 28px', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer', letterSpacing: '2px' },
  navSellBtn:     { background: 'transparent', color: GOLD, border: `1.5px solid ${GOLD}`, padding: '9px 20px', borderRadius: '30px', fontWeight: '800', cursor: 'pointer', letterSpacing: '1.5px', fontSize: '0.68rem' },
  heroContent:    { marginTop: '160px', padding: '0 20px', position: 'relative', zIndex: 2 },
  heroTitle:      { fontSize: 'clamp(4.5rem, 12vw, 7.5rem)', fontWeight: '900', color: GOLD, margin: 0, letterSpacing: '-3px' },
  heroSub:        { fontSize: '1.2rem', maxWidth: '850px', margin: '2rem auto 4rem', opacity: 0.85, lineHeight: '1.8' },
  subEmphasis:    { letterSpacing: '4px', textTransform: 'uppercase', fontSize: '0.9rem', fontWeight: 'bold', display: 'block', color: GOLD },
  heroGrid:       { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', maxWidth: '600px', margin: '0 auto' },
  goldHeroBtn:    { background: GOLD, color: 'black', padding: '1.4rem', borderRadius: '14px', fontWeight: '900' },
  snapHeroBtn:    { background: GOLD, color: 'black', padding: '1.4rem', borderRadius: '14px', fontWeight: '900' },
  outlineHeroBtn: { background: 'rgba(255,255,255,0.03)', color: 'white', border: `1px solid ${GOLD}66`, padding: '1.4rem', borderRadius: '14px', fontWeight: '900' },
  waitlistSection:{ marginTop: '4rem', padding: '20px', width: '100%', maxWidth: '500px', zIndex: 5 },
  formCard:       { background: '#0a1219', padding: '40px', borderRadius: '35px', border: '1px solid #1a2631', boxShadow: '0 25px 50px rgba(0,0,0,0.5)' },
  roleSwitcher:   { display: 'flex', background: '#050a0f', padding: '5px', borderRadius: '14px', marginBottom: '25px', border: '1px solid #1a2631' },
  roleBtn:        { flex: 1, padding: '12px', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '900', fontSize: '0.75rem', letterSpacing: '1px' },
  formStyle:      { display: 'flex', flexDirection: 'column', gap: '15px' },
  inputStyle:     { background: '#050a0f', border: '1px solid #1a2631', padding: '18px', borderRadius: '12px', color: 'white', fontSize: '1rem' },
  submitBtn:      { background: GOLD, color: 'black', padding: '20px', borderRadius: '12px', fontWeight: '900', border: 'none', cursor: 'pointer', fontSize: '1rem', marginTop: '10px' },
  modalOverlay:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  portalBox:      { background: DEEP_BLACK, padding: '40px', borderRadius: '20px', border: `2px solid ${GOLD}`, maxWidth: '400px', textAlign: 'center' },
  tryOnContainer: { width: '95%', height: '90%', position: 'relative' },
  portalBtn:      { background: GOLD, color: 'black', padding: '12px 24px', border: 'none', borderRadius: '12px', fontWeight: 'bold', margin: '10px', cursor: 'pointer', width: '100%' },
};
